import React, { useCallback, useEffect, useRef } from "react";
import { Search } from "lucide-react";

/**
 * Reusable Search Bar Component with Flexible Search Handling
 * @param {Object} props
 * @param {string} props.searchTerm - Current search term
 * @param {function} props.onChange - Callback to handle input changes
 * @param {function} props.onSearch - Callback to trigger search
 * @param {string} [props.placeholder] - Placeholder text for input
 * @param {string} [props.className] - Additional CSS classes
 */
const SearchBar = ({
  searchTerm,
  onChange,
  onSearch,
  placeholder = "Enter here...",
  className = "",
}) => {
  const previousTermRef = useRef(searchTerm || "");

  // Handle input change
  const handleChange = (e) => {
    onChange(e.target.value);
  };

  // Handle search submission
  const handleSearch = useCallback(() => {
    onSearch(searchTerm);
  }, [searchTerm, onSearch]);

  // Handle key press (specifically Enter key)
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  // Automatically restore full data when search input is cleared.
  useEffect(() => {
    const previousTerm = (previousTermRef.current || "").trim();
    const currentTerm = (searchTerm || "").trim();

    if (previousTerm !== "" && currentTerm === "") {
      onSearch("");
    }

    previousTermRef.current = searchTerm || "";
  }, [searchTerm, onSearch]);

  return (
    <div
      className={`flex items-center  border border-gray-300 rounded-lg overflow-hidden ${className} w-full md:w-[300px]`}
    >
      {/* Mobile: input with icon inside */}
      <div
        className="flex md:hidden items-center w-full max-w-[430px] h-9 pl-2 pr-2 "
        style={{ color: "#9CA3AF" }}
      >
        <div className="relative w-full h-full">
          <span className="absolute left-2 top-1/2 transform -translate-y-1/2">
            <Search className="h-5 w-5 text-gray-400" />
          </span>
          <input
            type="text"
            placeholder="Search here..."
            className="pl-10 pr-2 w-full h-full text-gray-500 focus:outline-none rounded"
            value={searchTerm}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            style={{ height: "100%" }}
          />
        </div>
      </div>
      {/* Desktop: show input and button */}
      <div className="hidden md:flex items-center w-full">
        <input
          type="text"
          placeholder={placeholder}
          className="pl-2 pr-2  w-full text-gray-500 focus:outline-none"
          value={searchTerm}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
        />
        <button
          onClick={handleSearch}
          className="px-2 py-3 bg-gray-50 text-gray-600 flex items-center space-x-2 border-l border-gray-300"
        >
          <Search className="h-5 w-5 text-gray-400" />
          <span className="text-text3 text-[14px] font-medium">Search</span>
        </button>
      </div>
    </div>
  );
};

export default SearchBar;
